import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getDomainInfo } from '../utils/tenantUtils';
import apiService from '../services/apiService';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const { currentTenant, user } = useAuth();
  const [forceUpdate, setForceUpdate] = useState(0);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);

  // Datos de respaldo por si falla la API
  const fallbackCompanyData = {
    chirakChile: {
      name: 'Chirak Turismo Chile',
      logo: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200',
      email: 'contacto@chirak.cl',
      phone: '+56 2 2345 6789',
      address: 'Av. Providencia 1234, Santiago, Chile',
      website: 'www.chirak.cl',
      description: 'Empresa líder en turismo en Chile',
      footerText: 'Chirak Turismo Chile - Descubre la magia de Chile',
      primaryColor: '#1E40AF',
      secondaryColor: '#DC2626',
      buttonBackColor: '#10B981',
      buttonCategorySelectedColor: '#3B82F6',
      buttonDownloadColor: '#2563EB',
      gradientFrom: 'from-blue-600',
      gradientTo: 'to-red-500',
      social: {
        facebook: 'https://facebook.com/chirakturismochile',
        twitter: 'https://twitter.com/chirakchile',
        instagram: 'https://instagram.com/chirakturismochile',
        linkedin: 'https://linkedin.com/company/chirak-turismo-chile',
        youtube: 'https://youtube.com/@chirakturismochile'
      }
    },
    chirakPeru: {
      name: 'Chirak Turismo Perú',
      logo: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200',
      email: 'contacto@chirak.pe',
      phone: '+51 1 234 5678',
      address: 'Av. José Larco 456, Lima, Perú',
      website: 'www.chirak.pe',
      description: 'Especialistas en turismo peruano',
      footerText: 'Chirak Turismo Perú - Explora la riqueza del Perú',
      primaryColor: '#DC2626',
      secondaryColor: '#FFFFFF',
      buttonBackColor: '#10B981',
      buttonCategorySelectedColor: '#3B82F6',
      buttonDownloadColor: '#2563EB',
      gradientFrom: 'from-red-600',
      gradientTo: 'to-white',
      social: {
        facebook: 'https://facebook.com/chirakturismoperu',
        twitter: 'https://twitter.com/chirakperu',
        instagram: 'https://instagram.com/chirakturismoperu',
        linkedin: 'https://linkedin.com/company/chirak-turismo-peru',
        youtube: 'https://youtube.com/@chirakturismoperu'
      }
    }
  };

  // Cargar empresas desde la API
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const response = await apiService.getCompanies();
        console.log('📡 Respuesta completa de la API:', response);
        
        // Transformar la respuesta de la API al formato esperado
        const companiesData = {};
        
        // Validar que la respuesta tenga datos
        const companiesArray = Array.isArray(response) ? response : (response?.companies || []);
        console.log('📋 Array de empresas a procesar:', companiesArray);
        
        if (companiesArray.length === 0) {
          console.warn('⚠️ No se encontraron empresas en la API, usando datos de respaldo');
          setCompanies(fallbackCompanyData);
          setLoading(false);
          return;
        }
        
        companiesArray.forEach(company => {
          console.log('🏢 Procesando empresa:', company);
          
          // Priorizar snake_case (formato de la BD) sobre camelCase
          const footerText = company.footer_text || company.footerText || '';
          const primaryColor = company.primary_color || company.primaryColor || '#1E40AF';
          const secondaryColor = company.secondary_color || company.secondaryColor || '#DC2626';
          
          // Construir URL completa del logo
          const getLogoUrl = (logoPath) => {
            if (!logoPath) return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200';
            if (logoPath.startsWith('http')) return logoPath;
            // Agregar timestamp para evitar caché del navegador
            const timestamp = new Date().getTime();
            return `${apiService.baseURL}${logoPath}?v=${timestamp}`;
          };
          
          const transformedCompany = {
            name: company.name || '',
            logo: getLogoUrl(company.logo),
            email: company.email || '',
            phone: company.phone || '',
            whatsapp_number: company.whatsapp_number || '',
            address: company.address || '',
            website: company.website || '',
            description: company.description || '',
            footerText: footerText,
            primaryColor: primaryColor,
            secondaryColor: secondaryColor,
            buttonBackColor: company.button_back_color || company.buttonBackColor || '#10B981',
            buttonCategorySelectedColor: company.button_category_selected_color || company.buttonCategorySelectedColor || '#3B82F6',
            buttonDownloadColor: company.button_download_color || company.buttonDownloadColor || '#2563EB',
            buttonMarketingKitColor: company.button_marketing_kit_color || company.buttonMarketingKitColor || '#10B981',
            buttonHotelColor: company.button_hotel_color || company.buttonHotelColor || '#F59E0B',
            gradientFrom: primaryColor.includes('1E40AF') || primaryColor.includes('3B82F6') ? 'from-blue-600' : 'from-red-600',
            gradientTo: secondaryColor.includes('DC2626') || secondaryColor.includes('EF4444') ? 'to-red-500' : 'to-white',
            tutorialVideoUrl: company.tutorial_video_url || company.tutorialVideoUrl || '',
            marketingKitUrl: company.marketing_kit_url || company.marketingKitUrl || '',
            hotelButtonUrl: company.hotel_button_url || company.hotelButtonUrl || '',
            showLanguageEs: company.show_language_es !== undefined ? company.show_language_es : true,
            showLanguageEn: company.show_language_en !== undefined ? company.show_language_en : true,
            showLanguagePt: company.show_language_pt !== undefined ? company.show_language_pt : true,
            social: {
              facebook: company.social_facebook || company.socialFacebook || '',
              twitter: company.social_twitter || company.socialTwitter || '',
              instagram: company.social_instagram || company.socialInstagram || '',
              linkedin: company.social_linkedin || company.socialLinkedin || '',
              youtube: company.social_youtube || company.socialYoutube || ''
            }
          };
          
          companiesData[company.id] = transformedCompany;
          console.log('✅ Empresa transformada:', transformedCompany);
          console.log('✅ Verificación de campos:', {
            name: transformedCompany.name,
            logo: transformedCompany.logo,
            email: transformedCompany.email,
            phone: transformedCompany.phone,
            whatsapp_number: transformedCompany.whatsapp_number,
            footerText: transformedCompany.footerText,
            socialFacebook: transformedCompany.social?.facebook
          });
        });
        
        setCompanies(companiesData);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🏢 Empresas cargadas desde API:', companiesData);
        }
        
      } catch (error) {
        console.error('Error cargando empresas:', error);
        
        // Usar datos de respaldo si falla la API
        setCompanies(fallbackCompanyData);
      } finally {
        setLoading(false);
      }
    };

    // Cargar empresas desde la API (intentar incluso sin usuario para LoginPage)
    loadCompanies();
  }, [user]);

  // Obtener la configuración de branding actual
  const getCurrentBranding = () => {
    const companyData = companies[currentTenant] || fallbackCompanyData[currentTenant];
    
    if (!companyData) {
      // Si no encontramos el tenant, usamos el primero disponible como fallback
      const firstCompany = Object.keys(companies)[0] || Object.keys(fallbackCompanyData)[0];
      return companies[firstCompany] || fallbackCompanyData[firstCompany] || fallbackCompanyData.chirakChile;
    }
    
    return companyData;
  };

  // Obtener clases de tema dinámico
  const getThemeClasses = () => {
    const branding = getCurrentBranding();
    return {
      gradient: `bg-gradient-to-r ${branding.gradientFrom} ${branding.gradientTo}`,
      primary: 'bg-blue-600 hover:bg-blue-700',
      secondary: 'bg-red-600 hover:bg-red-700',
      accent: 'text-blue-400',
      primaryText: 'text-blue-600',
      secondaryText: 'text-red-600',
      primaryBg: 'bg-blue-600',
      secondaryBg: 'bg-red-600'
    };
  };

  // Función para actualizar branding de una empresa
  const updateBranding = (companyId, newBranding) => {
    setCompanies(prev => ({
      ...prev,
      [companyId]: {
        ...prev[companyId],
        ...newBranding
      }
    }));
  };

  const value = {
    currentBranding: getCurrentBranding(),
    getCurrentBranding,
    themeClasses: getThemeClasses(),
    getThemeClasses,
    getDomainInfo,
    companies,
    companyBranding: companies, // Alias para compatibilidad
    loading,
    updateBranding,
    forceUpdate: () => setForceUpdate(prev => prev + 1),
    tenantInfo: getDomainInfo()
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('🎨 BrandingContext - Current tenant:', currentTenant);
    console.log('🎨 BrandingContext - Current branding:', getCurrentBranding());
  }

  // Actualizar el título del documento según el branding/tenant actual
  React.useEffect(() => {
    try {
      const branding = getCurrentBranding();
      const title = branding?.name ? `${branding.name} — Tarifario` : 'Tarifario Chirak';
      if (document && document.title !== title) document.title = title;
    } catch (err) {
      // no bloquear la app por fallos en el título
      console.warn('Unable to set document title from branding', err);
    }
  }, [currentTenant, companies]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;